import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, Tooltip, notification } from "antd";
import Swal from "sweetalert2";
import HashLoader from "react-spinners/ClipLoader";
import fetchSiteList from "../../../api/sites/fetchSiteList";
import DeleteIcon from "../../../assets/delete.svg";
import EditIcon from "../../../assets/edit.svg";

const emptyForm = { name: "", description: "", site_id: "", universal_code: "" };

const DepartmentList = () => {
  const url = process.env.REACT_APP_APP_BACK_URL;
  const isAuthenticated = useSelector((state) => state.user);
  const isSuperAdmin =
    String(isAuthenticated?.role || "").toLowerCase() === "superadmin";
  const [sites, setSites] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [blocked, setBlocked] = useState("");

  const authHeaders = () => {
    const headers = new Headers();
    headers.append("auth-token", localStorage.getItem("token"));
    headers.append("Content-Type", "application/json");
    return headers;
  };

  const defaultSiteId = (list) => (list.length === 1 ? list[0]._id : "");

  const loadRows = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${url}/api/department/list`, {
        headers: authHeaders(),
      });
      const json = await response.json();
      if (!json.success && json.error) {
        setBlocked(json.error);
        setRows([]);
        return;
      }
      setBlocked("");
      setRows(Array.isArray(json.data) ? json.data : []);
    } catch (error) {
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const siteData = await fetchSiteList();
      const list = siteData?.site_data || [];
      setSites(list);
      setForm((prev) => ({ ...prev, site_id: prev.site_id || defaultSiteId(list) }));
    })();
    loadRows();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, site_id: defaultSiteId(sites) });
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name,
      description: item.description || "",
      site_id: item.site_id,
      universal_code: item.universal_code || "",
    });
    setOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      notification.error({ message: "Failed", description: "Name is required", duration: 3 });
      return;
    }
    setIsSaving(true);
    try {
      const response = await fetch(
        editing ? `${url}/api/department/update/${editing._id}` : `${url}/api/department/add`,
        {
          method: editing ? "PUT" : "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            name,
            description: form.description.trim(),
            universal_code: form.universal_code.trim(),
            site_id: form.site_id,
          }),
        }
      );
      const json = await response.json();
      if (!json.success) {
        notification.error({
          message: "Failed",
          description: json.error || "Could not save the department",
          duration: 3,
        });
        return;
      }
      notification.success({
        message: "Success",
        description: editing ? "Department updated" : "Department added",
        duration: 3,
      });
      setOpen(false);
      setEditing(null);
      loadRows();
    } catch (error) {
      notification.error({
        message: "Failed",
        description: "Could not save the department",
        duration: 3,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This department will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    const response = await fetch(`${url}/api/department/delete/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    const json = await response.json();
    if (json.success) {
      setRows((prev) => prev.filter((item) => item._id !== id));
      Swal.fire("Deleted!", "Department deleted successfully!", "success");
    } else {
      Swal.fire("Error", json.error || "Could not delete", "error");
    }
  };

  return (
    <div className="general-dashboard">
      <div className="container-fluid">
        <div className="dashboard-top-row">
          <div className="row">
            <div className="col-lg-6 my-auto">
              <div className="user-wrap">
                <h3>Departments</h3>
                <p>Name and description for each site</p>
              </div>
            </div>
            <div className="col-lg-6 text-end">
              {!blocked && (
                <button type="button" className="btn btn-prim w-auto" onClick={openAdd}>
                  Add Department
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="table-wrap">
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Universal code</th>
                  {isSuperAdmin && <th>Site</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={isSuperAdmin ? 5 : 4} className="text-center">
                      <HashLoader color="#0b0d41" loading size={40} />
                    </td>
                  </tr>
                )}
                {!isLoading && blocked && (
                  <tr>
                    <td colSpan={isSuperAdmin ? 5 : 4} className="text-center">
                      {blocked}
                    </td>
                  </tr>
                )}
                {!isLoading && !blocked && rows.length === 0 && (
                  <tr>
                    <td colSpan={isSuperAdmin ? 5 : 4} className="text-center">
                      No departments yet
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  !blocked &&
                  rows.map((item) => (
                    <tr key={item._id}>
                      <td>{item.name}</td>
                      <td>{item.description || "—"}</td>
                      <td>{item.universal_code || "—"}</td>
                      {isSuperAdmin && <td>{item.site || "—"}</td>}
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => openEdit(item)}
                          >
                            <Tooltip title="Edit">
                              <img src={EditIcon} alt="edit" />
                            </Tooltip>
                          </button>
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => handleDelete(item._id)}
                          >
                            <Tooltip title="Delete">
                              <img src={DeleteIcon} alt="delete" />
                            </Tooltip>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Modal
        title={editing ? "Edit Department" : "Add Department"}
        open={open}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        centered
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label" htmlFor="department-name">
              Name
            </label>
            <input
              id="department-name"
              type="text"
              className="form-control"
              placeholder="Department name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="department-universal-code">
              Universal pickup code
            </label>
            <input
              id="department-universal-code"
              type="text"
              className="form-control"
              placeholder="Shared code for this department"
              value={form.universal_code}
              onChange={(e) => setForm({ ...form, universal_code: e.target.value })}
              readOnly={!isSuperAdmin}
              autoComplete="off"
            />
            <div className="form-text">
              {isSuperAdmin
                ? "People enter this on the department pickup page. Leave it blank when each locker has its own code."
                : "Universal code is read only. A super admin can change it."}
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="department-description">
              Description
            </label>
            <textarea
              id="department-description"
              className="form-control"
              placeholder="Description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          {isSuperAdmin && (
            <div className="mb-3">
              <label className="form-label" htmlFor="department-site">
                Site
              </label>
              <select
                id="department-site"
                className="form-control"
                value={form.site_id}
                onChange={(e) => setForm({ ...form, site_id: e.target.value })}
                required
              >
                <option value="">Choose Site</option>
                {sites.map((site) => (
                  <option key={site._id} value={site._id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="text-end">
            <button type="button" className="btn btn-light me-2" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-prim w-auto" disabled={isSaving}>
              {isSaving ? "Saving..." : editing ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DepartmentList;
